import { unixTimestamp } from '@/utility'
import { BillingPlan, TeamUserRole, type Team } from './schema'
import { differenceInDays, fromUnixTime, startOfDay } from 'date-fns'

/**
 * Returns the number of days until a team
 * billing plan expires.
 *
 * Explicitly returns null if there is no expiry.
 */
export function getBillingExpiryDays(
  expires: number | undefined | null,
  plan: BillingPlan,
): number | null {
  // Free plan never has an expiry date
  if (plan === BillingPlan.Free) return null

  // If there is no expiry
  if (!billingExpires(expires)) return null

  return Math.max(
    differenceInDays(startOfDay(fromUnixTime(expires)), startOfDay(new Date())),
    0,
  )
}

/** Is team billing expired */
export function isBillingExpired(
  expires: number | undefined | null,
  plan: BillingPlan,
) {
  if (plan === BillingPlan.Free) return false

  if (typeof expires !== 'number') return false

  if (expires === -1) return false

  return expires < unixTimestamp()
}

/**
 * Billing features determines what
 * teams can do on the platform based
 * on billing plan.
 */
export const billingFeatures = {
  team: {
    archival: 'teamArchival',
  },
} as const satisfies Record<string, Record<string, string>>

export type AllBillingFeatures = {
  [K in keyof typeof billingFeatures]: (typeof billingFeatures)[K][keyof (typeof billingFeatures)[K]]
}[keyof typeof billingFeatures]

/**
 * Mapping for team permissions. This
 * dictates what a team member can and
 * cannot do.
 */
const teamBillingFeatures: Record<
  AllBillingFeatures,
  Record<BillingPlan, boolean>
> = {
  [billingFeatures.team.archival]: {
    [BillingPlan.Enterprise]: true,
    [BillingPlan.Premium]: true,
    [BillingPlan.ProMonthly]: true,
    [BillingPlan.ProYearly]: true,
    [BillingPlan.Trial]: false,
    [BillingPlan.Free]: false,
  },
}

/**
 * Helper utility to determine if a team
 * has access to the billing feature specified.
 *
 * If a team's billing plan has expired, the utility
 * will return false.
 */
export function hasBillingFeature(
  team: Team | undefined | null,
  feature: AllBillingFeatures,
): boolean {
  if (!team) return false

  if (isBillingExpired(team.billingInfo.expires, team.billingInfo.plan))
    return false

  return teamBillingFeatures[feature][team.billingInfo.plan] ?? false
}

/** Utility helper to determine if a plan is non-paid. */
export function isFreePlan(plan: BillingPlan) {
  return [BillingPlan.Free, BillingPlan.Trial].includes(plan)
}

/**
 * Helper utility to determine if a billing expiry
 * timestamp is set to expire.
 */
export function billingExpires(
  expiry: number | undefined | null,
): expiry is number {
  if (typeof expiry !== 'number') return false
  if (expiry === -1) return false
  return true
}

/**
 * All team permissions available to check.
 * We only support a top level group for now,
 */
export const permissions = {
  team: {
    delete: 'deleteTeam',
    manage: 'manageTeam',
    manageOwners: 'manageOwners',
  },
  docs: {
    create: 'createDocs',
    edit: 'editDocs',
    delete: 'deleteDocs',
  },
} as const satisfies Record<string, Record<string, string>>

export type AllPermissions = {
  [K in keyof typeof permissions]: (typeof permissions)[K][keyof (typeof permissions)[K]]
}[keyof typeof permissions]

/**
 * Mapping for team permissions. This
 * dictates what a team member can and
 * cannot do.
 */
const teamPermissions: Record<AllPermissions, Record<TeamUserRole, boolean>> = {
  [permissions.team.manageOwners]: {
    [TeamUserRole.Owner]: true,
    [TeamUserRole.Admin]: false,
    [TeamUserRole.Editor]: false,
    [TeamUserRole.Viewer]: false,
  },
  [permissions.team.manage]: {
    [TeamUserRole.Owner]: true,
    [TeamUserRole.Admin]: true,
    [TeamUserRole.Editor]: false,
    [TeamUserRole.Viewer]: false,
  },
  [permissions.team.delete]: {
    [TeamUserRole.Owner]: true,
    [TeamUserRole.Admin]: false,
    [TeamUserRole.Editor]: false,
    [TeamUserRole.Viewer]: false,
  },
  [permissions.docs.create]: {
    [TeamUserRole.Owner]: true,
    [TeamUserRole.Admin]: true,
    [TeamUserRole.Editor]: true,
    [TeamUserRole.Viewer]: false,
  },
  [permissions.docs.edit]: {
    [TeamUserRole.Owner]: true,
    [TeamUserRole.Admin]: true,
    [TeamUserRole.Editor]: true,
    [TeamUserRole.Viewer]: false,
  },
  [permissions.docs.delete]: {
    [TeamUserRole.Owner]: true,
    [TeamUserRole.Admin]: true,
    [TeamUserRole.Editor]: true,
    [TeamUserRole.Viewer]: false,
  },
}

/**
 * Helper utility to determine if a team
 * member role has the permission specified.
 */
export function hasTeamPermission(
  role: TeamUserRole | undefined | null,
  permission: AllPermissions,
  throwError = false,
): boolean {
  const hasPermission = role
    ? (teamPermissions[permission][role] ?? false)
    : false

  if (!hasPermission && throwError)
    throw new Error(
      JSON.stringify({
        message: 'You do not have permissions to perform that operation',
        code: 'insufficient-perms',
        status: 403,
      }),
    )

  return hasPermission
}

/**
 * Helper shortcut to find a team member
 * in a team record.
 */
export function matchingTeamUser(uid: string, team: Team) {
  return team.users.find((u) => u.uid === uid)
}
